import React, { useEffect, useRef, useState } from 'react';
import { Loader, Modal, Select, Text, TextInput, UnstyledButton } from '@mantine/core';
import type { PaidRefundCandidate, PaymentTerminalView, PrinterView } from 'shared-models/browser';
import { Button } from '../../../../../components/Button';
import type { OrderView } from '../../../../../services/orders/orders.service';
import { paidOrderRefundsService } from '../../../../../services/payments/paid-order-refunds.service';
import { creditPaymentsService } from '../../../../../services/credit-payments/credit-payments.service';
import { printersService } from '../../../../../services/printers/printers.service';
import { formatMoney } from '../../../../../utils/money';
import classes from '../../receipt/transaction-receipt/receipt-print-selection-modal.module.scss';

type Props = {
  order: OrderView;
  tenantId: string;
  employeeSession: string;
  onClose: () => void;
  onReceipt?: (orderId: number) => void;
};

export const PaidOrderRefundDialog: React.FC<Props> = ({ order, tenantId, employeeSession, onClose, onReceipt }) => {
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState<PaidRefundCandidate[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [drawers, setDrawers] = useState<PrinterView[]>([]);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const [terminals, setTerminals] = useState<PaymentTerminalView[]>([]);
  const [terminalTarget, setTerminalTarget] = useState<string | null>(null);
  const [codeRequested, setCodeRequested] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [refundOrderId, setRefundOrderId] = useState<number | null>(null);
  const [voidConfirm, setVoidConfirm] = useState(false);

  useEffect(() => {
    let active = true;
    void paidOrderRefundsService.candidates(tenantId, order.id, employeeSession).then(async (items) => {
      const cashDrawers = items.some((item) => item.method === 'cash')
        ? (await printersService.listActive(tenantId, employeeSession)).filter((printer) => printer.cashDrawer)
        : [];
      const activeTerminals = items.some((item) => item.method === 'credit' && item.creditSource === 'top')
        ? await creditPaymentsService.listTerminals(tenantId, employeeSession) : [];
      if (!active) return;
      setCandidates(items);
      setSelectedIds(items.filter((item) => item.status === 'new')
        .map((item) => item.sourcePaymentlineId));
      setDrawers(cashDrawers);
      setTerminals(activeTerminals);
      if (cashDrawers.length === 1) setDrawerId(String(cashDrawers[0].id));
    }).catch(() => {
      if (active) setError('לא ניתן לטעון את התשלומים');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [tenantId, order.id, employeeSession]);

  const draftExists = candidates.some((item) => item.refundPaymentlineId !== null);
  const selected = candidates.filter((item) => selectedIds.includes(item.sourcePaymentlineId));
  const hasCash = selected.some((item) => item.method === 'cash');
  const hasTop = selected.some((item) => item.method === 'credit' && item.creditSource === 'top');
  const targets = terminals.flatMap((terminal) => terminal.pinpads.map((pinpad) => ({
    value: JSON.stringify([terminal.id, pinpad.pinpadId]),
    label: `${terminal.name} · ${pinpad.label || pinpad.pinpadId}`,
    terminalId: terminal.id,
    pinpadId: pinpad.pinpadId,
  })));
  const selectedTarget = targets.find((target) => target.value === terminalTarget);
  const amount = selected.reduce((sum, item) => sum + item.amount, 0);
  const eligible = candidates.filter((item) => item.status !== 'done' && item.status !== 'canceled_reversed' &&
    (item.method !== 'credit' ||
    (item.originalReference && (item.creditSource === 'top' || (item.terminalId && item.pinpadId)))));
  const allSelected = eligible.length > 0 && eligible.every((item) => selectedIds.includes(item.sourcePaymentlineId));
  const selectionLocked = busy || codeRequested || draftExists;

  const voidFailed = async () => {
    if (inFlight.current || !voidConfirm) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      await paidOrderRefundsService.voidFailed(tenantId, order.id, employeeSession);
      onClose();
    } catch {
      setError('לא ניתן לסגור את הזיכוי. יש לבדוק את מצב העסקה.');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const submit = async () => {
    if (inFlight.current || !selectedIds.length || (hasCash && drawers.length > 1 && !drawerId) ||
      (hasTop && (targets.length === 0 || (targets.length > 1 && !terminalTarget)))) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const result = await paidOrderRefundsService.refund(tenantId, order.id, employeeSession, {
        paymentlineIds: selectedIds,
        ...(codeRequested ? { code } : {}),
        ...(hasCash && drawerId ? { cashDrawerPrinterId: Number(drawerId) } : {}),
        ...(hasTop && selectedTarget ? { refundTerminalId: selectedTarget.terminalId,
          refundPinpadId: selectedTarget.pinpadId } : {}),
      });
      if (result.outcomes.some((outcome) => outcome.status !== 'approved')) {
        setError('חלק מהזיכוי לא הושלם. ההזמנה נשארה פתוחה לבדיקה ידנית.');
      } else {
        setRefundOrderId(result.refundOrderId);
      }
    } catch (caught) {
      if (caught instanceof Error && caught.message === 'REFUND_2FA_REQUIRED') {
        setCodeRequested(true);
      } else {
        setError('הזיכוי לא הושלם. יש לבדוק את סטטוס העסקה לפני ניסיון חוזר.');
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal opened onClose={() => { if (!inFlight.current) onClose(); }}
      closeOnClickOutside={!busy} closeOnEscape={!busy} title={`זיכוי שולחן ${order.tableNumber ?? order.orderCode}`}
      centered radius="md" dir="rtl" size="sm">
      <div className={classes.shell}>
        {voidConfirm ? <Text>יש לאשר מול חברת האשראי שהזיכוי לא בוצע. סגירת הבקשה לא תאפשר ניסיון זיכוי נוסף להזמנה זו.</Text> : null}
        {loading ? <Loader size="sm" /> : candidates.length === 0 ? <Text>אין תשלומים לזיכוי</Text> : <>
          {candidates.some((item) => item.status === 'canceled_reversed') &&
            <Text>בקשת הזיכוי נסגרה ללא ביצוע. לא ניתן לזכות שוב הזמנה זו.</Text>}
          <UnstyledButton type="button" className={classes.selectAll}
            onClick={() => setSelectedIds(allSelected ? [] : eligible.map((item) => item.sourcePaymentlineId))}
            disabled={selectionLocked || eligible.length === 0} aria-pressed={allSelected}>
            <span>בחר הכל</span>
            <span className={`${classes.checkbox} ${allSelected ? classes.checkboxSelected : ''}`} aria-hidden="true">
              {allSelected ? '✓' : ''}
            </span>
          </UnstyledButton>
          <div className={classes.list}>
            {candidates.map((item) => {
              const checked = selectedIds.includes(item.sourcePaymentlineId);
              const disabled = selectionLocked || !eligible.includes(item);
              return <UnstyledButton key={item.sourcePaymentlineId} type="button"
                className={`${classes.row} ${checked ? classes.rowSelected : ''}`}
                disabled={disabled} aria-pressed={checked}
                onClick={() => setSelectedIds((current) => checked
                  ? current.filter((id) => id !== item.sourcePaymentlineId)
                  : [...current, item.sourcePaymentlineId])}>
                <span>{item.method === 'credit' ? 'אשראי' : 'מזומן'} · {formatMoney(item.amount)} ₪
                  {item.status === 'done' ? ' · זוכה' : item.status === 'canceled_reversed' ? ' · נסגר ללא זיכוי' : ''}</span>
                <span className={`${classes.checkbox} ${checked ? classes.checkboxSelected : ''}`} aria-hidden="true">
                  {checked ? '✓' : ''}
                </span>
              </UnstyledButton>;
            })}
          </div>
          {selected.length > 0 && <Text fw={600}>סה״כ זיכוי: {formatMoney(amount)} ₪</Text>}
          {hasCash && drawers.length > 1 && <Select label="מגירת מזומן" data={drawers.map((drawer) =>
            ({ value: String(drawer.id), label: drawer.name }))} value={drawerId} onChange={setDrawerId} />}
          {hasTop && targets.length > 1 && <Select label="מסוף ופינפד לזיכוי" data={targets.map(({ value, label }) => ({ value, label }))}
            value={terminalTarget} onChange={setTerminalTarget} disabled={busy || codeRequested} />}
          {hasTop && !targets.length && <Text c="red">אין מסוף ופינפד פעילים לזיכוי אשראי</Text>}
          {codeRequested && !refundOrderId && <TextInput label="קוד אישור" inputMode="numeric"
            maxLength={6} value={code} onChange={(event) => setCode(event.currentTarget.value.replace(/\D/g, ''))}
            onContextMenu={(event) => event.preventDefault()}
            onSelect={(event) => { const input = event.currentTarget; input.setSelectionRange(input.value.length, input.value.length); }} />}
        </>}
        {error && <Text c="red" role="alert">{error}</Text>}
        {draftExists && candidates.some((item) => item.status === 'new') && !refundOrderId && !voidConfirm &&
          <Button variant="cancel" onClick={() => setVoidConfirm(true)} disabled={busy}>
            סגור זיכוי שלא בוצע
          </Button>}
        <div className={classes.actions}>
          <Button variant="cancel" fullWidth onClick={() => voidConfirm ? setVoidConfirm(false) : onClose()} disabled={busy}>ביטול</Button>
          {refundOrderId ? <Button variant="confirm" fullWidth onClick={() => { onReceipt?.(refundOrderId); onClose(); }}>קבלה</Button>
            : <Button variant="confirm" fullWidth onClick={voidConfirm ? voidFailed : submit} loading={busy} disabled={!voidConfirm && (loading || !selected.length ||
              (hasCash && drawers.length > 1 && !drawerId) ||
              (hasTop && (!targets.length || (targets.length > 1 && !terminalTarget))) ||
              (codeRequested && code.length !== 6))}>
              {voidConfirm ? 'אשר סגירת זיכוי' : codeRequested ? 'אשר זיכוי' : 'שלח קוד זיכוי'}
            </Button>}
        </div>
      </div>
    </Modal>
  );
};